import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import i18n from '@/i18n/config';

export type Language = 'de' | 'en' | 'sr';

const AVAILABLE_LANGUAGES: { code: Language; name: string; flag: string }[] = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'sr', name: 'Srpski', flag: '🇷🇸' },
];

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  availableLanguages: { code: Language; name: string; flag: string }[];
}

const storageKey = 'language-storage';

type PersistedLanguage = { language: Language };

const languageStorage: PersistStorage<PersistedLanguage> = {
  getItem: (name: string): StorageValue<PersistedLanguage> | null => {
    if (name !== storageKey) return null;
    try {
      const raw = localStorage.getItem(name);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { state?: { language?: string }; version?: number };
      const language = parsed?.state?.language as Language | undefined;
      if (language && AVAILABLE_LANGUAGES.some((l) => l.code === language)) {
        return { state: { language }, version: parsed?.version ?? 0 };
      }
      return null;
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: StorageValue<PersistedLanguage>): void => {
    localStorage.setItem(name, JSON.stringify(value));
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: 'de',
      availableLanguages: AVAILABLE_LANGUAGES,
      setLanguage: (lang: Language) => {
        i18n.changeLanguage(lang);
        set({ language: lang });
        localStorage.setItem('app_language', lang);
      },
    }),
    {
      name: storageKey,
      partialize: (state) => ({ language: state.language }),
      storage: languageStorage,
    }
  )
);
